import { isNullOrUndefined, filterArraysFromPreviousStepData, flattenPreserveArrays } from '@/utils/helpers';
import {
	TEST_RESULT_STATUSES,
	USER_CONFIGURATIONS_FIELDS_KEY,
	GENERAL_CONFIGURATIONS_FIELDS_KEY,
	CONFIGURATIONS_FIELDS_KEY,
	VIRTUAL_FIELDS,
	GLOBAL_RUN_DATA,
	CONTEXT_NODE_FIELD_KEY,
	PLY_ICON_SERVICE_SLUG,
} from '@/utils/workflow-node.const';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import ParamsService from '@/utils/ParamsService';
import PlacementService from '@/utils/placement-service';
import AppService from '@/utils/AppService';
import WorkflowService from '@/utils/WorkflowService';
/* Private */

// skip this if its multiple results only -- maybe we want to return it anyway? and the DataInputMenu will know when to hide it
const getNonTreeStepItem = (key, title, items, serviceSlug, itemKey = '') => {
	const mappedFieldNodeInfo = {
		nodeTitle: title,
		serviceSlug,
		isMultiple: false,
		testResultStatus: TEST_RESULT_STATUSES.SUCCESS,
	};

	const mappedFieldsItem = {
		nodeInfo: mappedFieldNodeInfo,
		key,
		itemKey,
		items: flattenPreserveArrays(items),
	};
	return mappedFieldsItem;
};

const getPlacementNodeTitle = (placement, service) => `Button on ${service.service_name} ${placement.item_name}`;
const isRootNodeOfWebhook = (service_slug) => AppService.isSlugOfWebhook(service_slug);
const showConfigurationsAndGlobals = (workflow) => !WorkflowService.isOfTypeSubFlow(workflow);
const needToAddElementStep = (service_slug, currentElement) =>
	isRootNodeOfWebhook(service_slug) &&
	currentElement &&
	currentElement.action_context_node === null &&
	currentElement.metadata.testValues?.values;

/* Public */

export const getDataSourcesItems = (
	parentNodeIds,
	appInfoGetter,
	nodeDataGetter,
	appActionsIdsGetter,
	actionInfoGetter,
	getPlacementGetter,
	getGlobalWorkflowData,
	elementContextualDataGetter,
	getPreviousStepDataForNodeGetter,
	isMultipleOnly,
	currentElement,
	searchString,
	itemsCollapsedStatus,
	generalSetupFields,
	userSetupFields,
	isConfigurationsOn, // featureFlag
	workflow,
	currentNode
) => {
	let resultArr = [];
	const isCurrentNodeNotification = !!currentNode.notification;
	if (showConfigurationsAndGlobals(workflow) && !isCurrentNodeNotification) {
		resultArr.push({
			...getNonTreeStepItem(GLOBAL_RUN_DATA, 'Runtime & user attributes', getGlobalWorkflowData, PLY_ICON_SERVICE_SLUG),
			isGlobal: true,
		});
	}

	if (isConfigurationsOn && showConfigurationsAndGlobals(workflow)) {
		if (generalSetupFields) {
			resultArr.push({
				...getNonTreeStepItem(
					GENERAL_CONFIGURATIONS_FIELDS_KEY,
					'General setup',
					generalSetupFields,
					PLY_ICON_SERVICE_SLUG,
					CONFIGURATIONS_FIELDS_KEY
				),
				workflowId: workflow.id,
				isConfiguration: true,
			});
		}

		if (userSetupFields) {
			resultArr.push({
				...getNonTreeStepItem(
					USER_CONFIGURATIONS_FIELDS_KEY,
					'User setup',
					userSetupFields,
					PLY_ICON_SERVICE_SLUG,
					CONFIGURATIONS_FIELDS_KEY
				),
				workflowId: workflow.id,
				isConfiguration: true,
			});
		}
	}

	parentNodeIds.forEach((nodeId) => {
		let previousStepData = flattenPreserveArrays(getPreviousStepDataForNodeGetter(nodeId, false));
		if (!nodeId || isNullOrUndefined(previousStepData)) {
			return;
		}

		const nodeData = nodeDataGetter(nodeId);
		if (!nodeData) {
			return;
		}

		const appInfo = appInfoGetter(nodeData.service);
		if (!appInfo) {
			return;
		}
		const numOfResults = 0;

		const obj = { key: nodeData.uuid };

		if (isMultipleOnly) {
			previousStepData = filterArraysFromPreviousStepData(previousStepData);
		}

		let nodeTitle = nodeData.node_title;
		let serviceSlug = appInfo.service_slug;
		const actionInfo = actionInfoGetter(nodeData.action);

		if (isRootNodeOfWebhook(appInfo.service_slug)) {
			if (currentElement) {
				const placement = getPlacementGetter(currentElement.placement);
				const elementService = appInfoGetter(placement.service);
				nodeTitle = getPlacementNodeTitle(placement, elementService);
				serviceSlug = elementService.service_slug;
			} else {
				serviceSlug = PLY_ICON_SERVICE_SLUG;
			}
		} else {
			const appActionsIds = appActionsIdsGetter[nodeData.service];
			if (!appActionsIds) {
				return;
			}
		}

		if (!nodeTitle) {
			nodeTitle = actionInfo ? actionInfo.title : appInfo.service_name;
		}

		obj.nodeInfo = {
			nodeTitle,
			serviceSlug,
			testResultStatus: nodeData.test_result_status,
			isMultiple: actionInfo?.is_multiple,
			hasUserSchema: actionInfo?.has_user_schema,
			skipMappingKeys: actionInfo?.has_user_schema ? ParamsService.getSkipKeysForMapping(appInfo, actionInfo) : [],
			userSchemaMappingsSourceFieldUuid: nodeData.user_schema_mappings_source_field_uuid,
			numOfResults,
			noun: actionInfo?.noun,
		};
		obj.items = previousStepData;
		obj.outputLabels = WorkflowNodeService.getTestResponseOutputLabels(nodeData);

		resultArr.push(obj);

		if (needToAddElementStep(appInfo.service_slug, currentElement)) {
			const placement = getPlacementGetter(currentElement.placement);
			const elementPlacementService = appInfoGetter(placement.service);
			resultArr.push({
				key: CONTEXT_NODE_FIELD_KEY,
				nodeInfo: {
					nodeTitle: PlacementService.getContextualVirtualNodeTitle(elementPlacementService),
					serviceSlug: elementPlacementService.service_slug,
					isMultiple: false,
					testResultStatus: TEST_RESULT_STATUSES.SUCCESS,
				},
				items: flattenPreserveArrays(elementContextualDataGetter),
			});
		}
	});

	resultArr = resultArr.map((val) => {
		const testResult = val.nodeInfo.testResultStatus;
		const validTestResult = [TEST_RESULT_STATUSES.SKIPPED, TEST_RESULT_STATUSES.SUCCESS, undefined].includes(testResult); // undefined is here for the mappedFields.
		const isActive = !!searchString && !itemsCollapsedStatus[val.key] && validTestResult;
		return {
			...val,
			active: isActive,
		};
	}); // if searchString is filled, force to expand all list groups

	const sort = (itemA, itemB) => {
		if (VIRTUAL_FIELDS.includes(itemA.key) || VIRTUAL_FIELDS.includes(itemB.key)) {
			const indexA = VIRTUAL_FIELDS.indexOf(itemA);
			const indexB = VIRTUAL_FIELDS.indexOf(itemB);
			if (indexA === -1) {
				return 1;
			}
			if (indexB === -1) {
				return -1;
			}
			return indexA > indexB ? 1 : -1;
		}
		const indexA = parentNodeIds.indexOf(itemA);
		const indexB = parentNodeIds.indexOf(itemB);
		return indexA - indexB;
	};
	return resultArr.sort(sort);
};

const DataInputService = {
	getDataSourcesItems,
};

export default DataInputService;
